import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Divider,
  useTheme,
  Checkbox,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import FormTextField from "../../../../../../views/components/common/form/FormTextField";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import AppSearchDropDown2 from "../../../../../../views/components/common/filter/AppSearchDropdown2";
import { Customer } from "../../../../../../models/Customer/Customer";
import { useStores } from "../../../../use-stores";
import { FilterValue } from "../../../../../../models/Filters/FilterValue";
import {
  StoreAdminPoc,
  StoreDetailsModel,
} from "../../../../../../models/Swagstore/StoreDetailsModel";
import { observer } from "mobx-react";
import CustomToggleSwitch from "../../../../../../views/components/common/switch/Switch";
import { EmployeeRoles } from "../../../../../../models/Employees/EmployeeRoles";

interface StoreSettingsProps {
  settings: StoreDetailsModel;
  customers?: Customer[];
  isLoading?: boolean;
  onSettingChange: (key: string, value: any) => void;
  onSaveChanges: () => void;
  errors?: Partial<
    Record<"store_name" | "customer_id" | "store_privacy" | "domains", string>
  >;
}

const ADMIN_POC_SEARCH_DEBOUNCE_MS = 400;

const StoreSettings: React.FC<StoreSettingsProps> = ({
  settings,
  customers = [],
  isLoading = false,
  onSettingChange,
  onSaveChanges,
  errors = {},
}) => {
  const theme = useTheme();
  const { customerStore, swagStoreStore, userStore, TaskTrackerStore } =
    useStores();
  const [adminPocSearchText, setAdminPocSearchText] = useState("");
  const [selectedAdminPocs, setSelectedAdminPocs] = useState<StoreAdminPoc[]>(
    settings.admin_pocs || [],
  );
  const adminPocSearchDebounceRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  useEffect(() => {
    setSelectedAdminPocs(settings.admin_pocs || []);
  }, [settings.admin_pocs]);

  useEffect(() => {
    return () => {
      if (adminPocSearchDebounceRef.current) {
        clearTimeout(adminPocSearchDebounceRef.current);
      }
    };
  }, []);

  const adminSearchResults = useMemo(
    () =>
      (TaskTrackerStore.admins || [])
        .filter(
          (admin) =>
            !selectedAdminPocs.some((selected) => selected.id === admin.id),
        )
        .map((admin) => ({
          id: admin.id.toString(),
          value: admin.name || admin.email || `Admin ${admin.id}`,
        })),
    [TaskTrackerStore.admins, selectedAdminPocs],
  );

  const syncAdminPocs = (admins: StoreAdminPoc[]) => {
    setSelectedAdminPocs(admins);
    onSettingChange(
      "admin_pocs",
      admins.map((admin) => ({
        id: admin.id,
        name: admin.name,
        email: admin.email,
      })),
    );
    onSettingChange(
      "admin_poc_ids",
      admins.map((admin) => admin.id),
    );
  };

  const handleAdminPocSearch = (searchText?: string) => {
    const query = searchText || "";
    setAdminPocSearchText(query);

    if (adminPocSearchDebounceRef.current) {
      clearTimeout(adminPocSearchDebounceRef.current);
      adminPocSearchDebounceRef.current = null;
    }

    const trimmed = query.trim();
    if (trimmed.length < 2) return;

    adminPocSearchDebounceRef.current = setTimeout(() => {
      try {
        TaskTrackerStore.getAdminBySearch(trimmed);
      } catch (error) {
        console.error("Error searching admins:", error);
      }
    }, ADMIN_POC_SEARCH_DEBOUNCE_MS);
  };

  const handleAdminPocSelect = (selectedItems: FilterValue[]) => {
    if (!selectedItems.length) return;
    const selectedId = Number(selectedItems[0].id);
    const matchedAdmin = TaskTrackerStore.admins.find(
      (admin) => admin.id === selectedId,
    );
    if (!matchedAdmin) return;
    if (selectedAdminPocs.some((admin) => admin.id === matchedAdmin.id)) return;

    syncAdminPocs([
      ...selectedAdminPocs,
      {
        id: matchedAdmin.id,
        name:
          matchedAdmin.name || matchedAdmin.email || `Admin ${matchedAdmin.id}`,
        email: matchedAdmin.email || undefined,
      },
    ]);
    setAdminPocSearchText("");
  };

  const handleAdminPocDelete = (adminId: number) => {
    syncAdminPocs(selectedAdminPocs.filter((admin) => admin.id !== adminId));
  };

  return (
    <Box>
      <CustomTypography
        variant="heading6"
        fontWeight={600}
        style={{ marginBottom: "12px" }}
      >
        Branding Settings
      </CustomTypography>
      <CustomTypography
        variant="heading9"
        color={theme.palette.grey[600]}
        style={{ marginBottom: "12px" }}
      >
        Customize your store's visuals & Elements
      </CustomTypography>

      <Divider sx={{ my: 3, width: "100%" }} />

      <Box
        sx={{
          boxShadow: `${theme.palette.customColors.shadowVeryLight} 0px 6px 24px 0px, ${theme.palette.customColors.shadowLightBorder} 0px 0px 0px 1px`,
          p: 2.2,
          borderRadius: 2,
        }}
      >
        <CustomTypography
          variant="body1"
          fontWeight={600}
          style={{ marginBottom: "14px" }}
        >
          Store Identity
        </CustomTypography>

        <CustomTypography
          variant="body2"
          fontWeight={600}
          style={{ marginBottom: "8px", color: theme.palette.grey[800] }}
        >
          Store Name
        </CustomTypography>

        <Box style={{ marginBottom: "0rem" }}>
          <FormTextField
            label="Store Name"
            placeholder="Enter store name"
            value={settings.store_name || ""}
            onChange={(id, value) => onSettingChange("store_name", value || "")}
            sx={{ marginBottom: "6px" }}
            backgroundColor={
              errors.store_name
                ? "transparent"
                : theme.palette.customColors.grayBackGroundColor
            }
            borderColor={theme.palette.customColors.greyBorder}
            error={Boolean(errors.store_name)}
            errorMessage={errors.store_name}
          />
        </Box>

        <Box>
          <CustomTypography
            variant="body3"
            fontWeight={600}
            style={{
              marginBottom: "8px",
              marginTop: "12px",
              color: theme.palette.grey[800],
            }}
          >
            Company Name
          </CustomTypography>

          <AppSearchDropDown2
            title=""
            value={
              settings.customer_id
                ? [
                    {
                      id: settings.customer_id?.toString() || "",
                      value: settings.customer_name || "",
                    },
                  ]
                : []
            }
            list={
              customers?.map((customer: Customer) => ({
                id: customer.id?.toString() || "",
                value: customer.name || "",
              })) || []
            }
            handleSelect={(selectedItems: FilterValue[]) => {
              if (selectedItems.length > 0) {
                onSettingChange("customer_id", Number(selectedItems[0].id));
                onSettingChange("customer_name", selectedItems[0].value);
              } else {
                onSettingChange("customer_id", "");
                onSettingChange("customer_name", "");
              }
            }}
            handleSearch={async (searchText?: string) => {
              customerStore.searchCustomers({
                page_no: 1,
                search_text: searchText || "",
              });
            }}
            isMultiSelect={false}
            backgroundColor={theme.palette.customColors.grayBackGroundColor}
            error={Boolean(errors.customer_id)}
            errorMessage={errors.customer_id}
          />
        </Box>
      </Box>

      {/* <Divider sx={{ my: 3 }} /> */}

      {/* Store Privacy Card */}
      <Box
        sx={{
          mt: 3,
          mb: 3,
          boxShadow: `${theme.palette.customColors.shadowVeryLight} 0px 6px 24px 0px, ${theme.palette.customColors.shadowLightBorder} 0px 0px 0px 1px`,
          p: 2.2,
          borderRadius: 2,
        }}
      >
        <CustomTypography
          variant="body1"
          fontWeight={600}
          style={{ marginBottom: "16px" }}
        >
          Store Privacy
        </CustomTypography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <CustomTypography
              variant="body2"
              fontWeight={600}
              style={{ marginBottom: "4px", color: theme.palette.grey[800] }}
            >
              Private Store
            </CustomTypography>
            <CustomTypography
              variant="body2"
              style={{ color: theme.palette.grey[800] }}
              fontWeight={400}
            >
              Restrict access to people with your company email domain.
            </CustomTypography>
          </Box>
          <Box sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}>
            <CustomToggleSwitch
              checked={(() => {
                const isPublic = settings.is_public as any; // Handle 0/1 from backend
                return !(
                  isPublic === true ||
                  isPublic === 1 ||
                  isPublic === "1"
                );
              })()}
              onChange={(e) => {
                onSettingChange("is_public", !e.target.checked);
              }}
              activeColor={theme.palette.customColors?.purple}
              inactiveColor={theme.palette.customColors.switchTrackGray}
              sx={{
                width: "30px",
                height: "25px",
                "& .MuiFormControlLabel-root": {
                  marginLeft: "0px",
                },
              }}
            />
          </Box>
        </Box>

        {errors.store_privacy && (
          <CustomTypography
            variant="body3"
            style={{ color: "#d32f2f", marginTop: "8px" }}
          >
            {errors.store_privacy}
          </CustomTypography>
        )}

        <CustomTypography
          variant="body2"
          fontWeight={600}
          style={{
            marginTop: "14px",
            marginBottom: "4px",
            color: theme.palette.grey[800],
          }}
        >
          Enter your company domain email
        </CustomTypography>

        <Box style={{ marginBottom: "0rem" }}>
          <FormTextField
            label=""
            placeholder=""
            value={settings.domains?.[0]?.domain || ""}
            onChange={(id, value) =>
              onSettingChange("domains", [{ domain: value || "" }])
            }
            sx={{ marginBottom: "0px", marginTop: 1 }}
            backgroundColor={(() => {
              const isPublic = settings.is_public as any; // Handle 0/1 from backend
              return (isPublic === false ||
                isPublic === 0 ||
                isPublic === "0") &&
                errors.domains
                ? "transparent"
                : theme.palette.customColors.grayBackGroundColor;
            })()}
            borderColor={(() => {
              const isPublic = settings.is_public as any; // Handle 0/1 from backend
              return (isPublic === false ||
                isPublic === 0 ||
                isPublic === "0") &&
                errors.domains
                ? theme.palette.customColors.errorRed
                : theme.palette.customColors.greyBorder;
            })()}
            disabled={(() => {
              const isPublic = settings.is_public as any; // Handle 0/1 from backend
              return isPublic === true || isPublic === 1 || isPublic === "1";
            })()}
            error={(() => {
              const isPublic = settings.is_public as any; // Handle 0/1 from backend
              return (
                (isPublic === false || isPublic === 0 || isPublic === "0") &&
                Boolean(errors.domains)
              );
            })()}
            errorMessage={(() => {
              const isPublic = settings.is_public as any; // Handle 0/1 from backend
              return isPublic === false || isPublic === 0 || isPublic === "0"
                ? errors.domains
                : "";
            })()}
          />
        </Box>
      </Box>

      {/* Authentication Methods Card */}
      <Box
        sx={{
          mt: 3,
          mb: 3,
          boxShadow: `${theme.palette.customColors.shadowVeryLight} 0px 6px 24px 0px, ${theme.palette.customColors.shadowLightBorder} 0px 0px 0px 1px`,
          p: 2.2,
          borderRadius: 2,
        }}
      >
        <CustomTypography
          variant="body1"
          fontWeight={600}
          style={{ marginBottom: "16px" }}
        >
          Authentication Methods
        </CustomTypography>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {swagStoreStore.authenticationMethods.length === 0 ? (
            <CustomTypography
              variant="body2"
              style={{ color: theme.palette.grey[600] }}
            >
              {swagStoreStore.loadingAuthenticationMethods
                ? "Loading authentication methods..."
                : "No authentication methods available"}
            </CustomTypography>
          ) : (
            swagStoreStore.authenticationMethods?.map((method) => {
              const isChecked =
                swagStoreStore.selectedAuthenticationMethods?.includes(
                  method.id,
                );
              return (
                <FormControlLabel
                  key={method.id}
                  control={
                    <Checkbox
                      checked={isChecked}
                      onChange={() => {
                        const updatedMethods = isChecked
                          ? swagStoreStore.selectedAuthenticationMethods.filter(
                              (id) => id !== method.id,
                            )
                          : [
                              ...swagStoreStore.selectedAuthenticationMethods,
                              method.id,
                            ];
                        swagStoreStore.setSelectedAuthenticationMethods(
                          updatedMethods,
                        );
                        onSettingChange(
                          "authentication_methods",
                          updatedMethods,
                        );
                      }}
                      sx={{
                        color: theme.palette.customColors?.purple,
                        "&.Mui-checked": {
                          color: theme.palette.customColors?.purple,
                        },
                      }}
                    />
                  }
                  label={
                    <CustomTypography
                      variant="body2"
                      style={{ color: theme.palette.grey[800] }}
                    >
                      {method.name || method.label}
                    </CustomTypography>
                  }
                />
              );
            })
          )}
        </Box>
      </Box>

      <Box
        sx={{
          mt: 3,
          mb: 3,
          boxShadow: `${theme.palette.customColors.shadowVeryLight} 0px 6px 24px 0px, ${theme.palette.customColors.shadowLightBorder} 0px 0px 0px 1px`,
          p: 2.2,
          borderRadius: 2,
        }}
      >
        <CustomTypography
          variant="body1"
          fontWeight={600}
          style={{ marginBottom: "16px" }}
        >
          Admin POCs
        </CustomTypography>

        <AppSearchDropDown2
          title=""
          value={[]}
          list={adminSearchResults}
          handleSelect={handleAdminPocSelect}
          handleSearch={handleAdminPocSearch}
          isMultiSelect={false}
          backgroundColor={theme.palette.customColors.grayBackGroundColor}
          showSelection={false}
          minWidth="100%"
        />

        {adminPocSearchText.trim().length > 0 &&
        adminPocSearchText.trim().length < 2 ? (
          <CustomTypography
            variant="body3"
            style={{
              color: theme.palette.grey[600],
              marginTop: "8px",
            }}
          >
            Type at least 2 characters to search admins.
          </CustomTypography>
        ) : null}

        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 1.25, mt: 2 }}
        >
          {selectedAdminPocs.length === 0 ? (
            <CustomTypography
              variant="body2"
              style={{ color: theme.palette.grey[600] }}
            >
              No admin POCs added yet.
            </CustomTypography>
          ) : (
            selectedAdminPocs.map((admin) => (
              <Box
                key={admin.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: `1px solid ${theme.palette.customColors.greyBorder}`,
                  borderRadius: 2,
                  px: 1.5,
                  py: 1,
                  backgroundColor:
                    theme.palette.customColors.grayBackGroundColor,
                }}
              >
                <Box sx={{ minWidth: 0, pr: 1 }}>
                  <CustomTypography
                    variant="body2"
                    fontWeight={600}
                    style={{ color: theme.palette.grey[800] }}
                  >
                    {admin.name}
                  </CustomTypography>
                  {admin.email ? (
                    <CustomTypography
                      variant="body3"
                      style={{ color: theme.palette.grey[600] }}
                    >
                      {admin.email}
                    </CustomTypography>
                  ) : null}
                </Box>
                <IconButton
                  size="small"
                  onClick={() => handleAdminPocDelete(admin.id)}
                  aria-label={`Remove ${admin.name}`}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))
          )}
        </Box>
      </Box>

      {/* Store Domain Card */}
      {/* <Box
        sx={{
          mb: 3,
          boxShadow: `${theme.palette.customColors.shadowVeryLight} 0px 6px 24px 0px, ${theme.palette.customColors.shadowLightBorder} 0px 0px 0px 1px`,
          p: 2.2,
          borderRadius: 2,
        }}
      >
        <CustomTypography
          variant="body1"
          fontWeight={600}
          style={{ marginBottom: "6px" }}
        >
          Store Domain
        </CustomTypography>

        <CustomTypography
          variant="body3"
          fontWeight={400}
          style={{ marginBottom: "16px" }}
        >
          Choose how your store URL appears:
        </CustomTypography>

        <RadioGroup
          value={settings.store_domain}
          onChange={(e) => onSettingChange("store_domain", e.target.value)}
        >

          <Box
            onClick={() => onSettingChange("store_domain", "default")}
            sx={{
              mb: 2,
              p: 2,
              borderRadius: "8px",
              border:
                settings.store_domain === "default"
                  ? `2px solid ${theme.palette.customColors?.purple}`
                  : `1px solid ${theme.palette.customColors.lightGray}`,
              backgroundColor:
                settings.store_domain === "default"
                  ? theme.palette.customColors.blueLightBackground
                  : theme.palette.customColors.white,
              cursor: "pointer",
              "&:hover": {
                borderColor:
                  settings.store_domain === "default"
                    ? theme.palette.customColors?.purple
                    : theme.palette.customColors?.greyBorder,
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <Radio
                value="default"
                checked={settings.store_domain === "default"}
                sx={{
                  color:
                    settings.store_domain === "default"
                      ? theme.palette.customColors?.purple
                      : theme.palette.customColors.mediumGray,
                  "&.Mui-checked": {
                    color: theme.palette.customColors?.purple,
                  },
                  padding: "4px",
                }}
              />
              <Box sx={{ flex: 1 }}>
                <CustomTypography
                  variant="body3"
                  fontWeight={settings.store_domain === "default" ? 600 : 400}
                  style={{
                    color:
                      settings.store_domain === "default"
                        ? theme.palette.customColors?.purple
                        : theme.palette.customColors.blackText,
                    marginBottom: "4px",
                  }}
                >
                  Default
                </CustomTypography>
                <CustomTypography
                  variant="body4"
                  style={{ color: theme.palette.grey[600] }}
                >
                  Exp: swageazy.com - yourcomanyname
                </CustomTypography>
              </Box>
            </Box>
          </Box>

          <Box
            onClick={() => onSettingChange("store_domain", "semi-customized")}
            sx={{
              mb: 2,
              p: 2,
              borderRadius: "8px",
              border:
                settings.store_domain === "semi-customized"
                  ? `2px solid ${theme.palette.customColors?.purple}`
                  : "1px solid #E5E7EB",
              backgroundColor:
                settings.store_domain === "semi-customized"
                  ? "#EFF6FF"
                  : "#FFFFFF",
              cursor: "pointer",
              "&:hover": {
                borderColor:
                  settings.store_domain === "semi-customized"
                    ? theme.palette.customColors?.purple
                    : theme.palette.customColors.switchTrackGray,
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <Radio
                value="semi-customized"
                checked={settings.store_domain === "semi-customized"}
                sx={{
                  color:
                    settings.store_domain === "semi-customized"
                      ? theme.palette.customColors?.purple
                      : theme.palette.customColors.mediumGray,
                  "&.Mui-checked": {
                    color: theme.palette.customColors?.purple,
                  },
                  padding: "4px",
                }}
              />
              <Box sx={{ flex: 1 }}>
                <CustomTypography
                  variant="body3"
                  fontWeight={
                    settings.store_domain === "semi-customized" ? 600 : 400
                  }
                  style={{
                    color:
                      settings.store_domain === "semi-customized"
                        ? theme.palette.customColors?.purple
                        : theme.palette.customColors.blackText,
                    marginBottom: "4px",
                  }}
                >
                  Semi Customized
                </CustomTypography>
                <CustomTypography
                  variant="body4"
                  style={{ color: theme.palette.grey[600] }}
                >
                  Exp: yourcomanyname - swageazy.com
                </CustomTypography>
              </Box>
            </Box>
          </Box>

          <Box
            onClick={() => onSettingChange("store_domain", "fully-customized")}
            sx={{
              p: 2,
              borderRadius: "8px",
              border:
                settings.store_domain === "fully-customized"
                  ? `2px solid ${theme.palette.customColors?.purple}`
                  : "1px solid #E5E7EB",
              backgroundColor:
                settings.store_domain === "fully-customized"
                  ? "#EFF6FF"
                  : "#FFFFFF",
              cursor: "pointer",
              "&:hover": {
                borderColor:
                  settings.store_domain === "fully-customized"
                    ? theme.palette.customColors?.purple
                    : theme.palette.customColors.switchTrackGray,
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <Radio
                value="fully-customized"
                checked={settings.store_domain === "fully-customized"}
                sx={{
                  color:
                    settings.store_domain === "fully-customized"
                      ? theme.palette.customColors?.purple
                      : theme.palette.customColors.mediumGray,
                  "&.Mui-checked": {
                    color: theme.palette.customColors?.purple,
                  },
                  padding: "4px",
                }}
              />
              <Box sx={{ flex: 1 }}>
                <CustomTypography
                  variant="body3"
                  fontWeight={
                    settings.store_domain === "fully-customized" ? 600 : 400
                  }
                  style={{
                    color:
                      settings.store_domain === "fully-customized"
                        ? theme.palette.customColors?.purple
                        : theme.palette.customColors.blackText,
                    marginBottom: "4px",
                  }}
                >
                  Fully Customized
                </CustomTypography>
                <CustomTypography
                  variant="body4"
                  style={{ color: theme.palette.grey[600] }}
                >
                  Exp: yourcompanyname.com
                </CustomTypography>
              </Box>
            </Box>
          </Box>
        </RadioGroup>
      </Box> */}

      <Box>
        <AppButton
          label="Save Changes"
          onClick={onSaveChanges}
          customStyles={{
            mb: 10,
            width: "100%",
            backgroundColor: theme.palette.customColors?.darkBlackBorder,
            color: theme.palette.customColors?.white,
            "&:hover": {
              backgroundColor: theme.palette.customColors?.darkBlackBorder,
              color: theme.palette.customColors?.white,
            },
            "&:disabled": {
              backgroundColor: theme.palette.customColors?.greyBorder,
              color: theme.palette.customColors?.white,
            },
          }}
          variant="primaryBlock"
          disabled={
            isLoading ||
            (!swagStoreStore.storeId
              ? !userStore.hasPermission(EmployeeRoles.CREATE_SWAGSTORE)
              : !userStore.hasPermission(EmployeeRoles.EDIT_SWAGSTORE))
          }
        />
      </Box>
    </Box>
  );
};

export default observer(StoreSettings);
